import React from 'react';
import styled, { keyframes } from 'styled-components';

/* ── Theme ─────────────────────────────────────────────────── */
const GOLD       = '#C9952A';
const GOLD_LIGHT = '#E8B84B';
const CREAM      = '#FAF6ED';
const WARM_WHITE = '#FFFCF5';
const TEXT       = '#2A1F0E';
const TEXT_MID   = '#6B5540';

const float = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

const shimmer = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

/* ── Icon helper ────────────────────────────────────────────── */
const IconWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(201,149,42,0.1), rgba(232,184,75,0.06));
  border: 1.5px solid rgba(201,149,42,0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.1rem;
  flex-shrink: 0;
`;

const GoldIcon = ({ children }) => (
  <IconWrap>
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none"
         stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  </IconWrap>
);

const PageContainer = styled.div`
  background: ${CREAM};
  overflow: hidden;
`;

const Hero = styled.section`
  position: relative;
  min-height: 72vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem 8rem;
  background: linear-gradient(135deg, #081A10 0%, #0D2216 55%, #081A10 100%);
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 88%, 0 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 50% -10%, rgba(201,149,42,0.28) 0%, transparent 55%),
      radial-gradient(ellipse at 5%  90%, rgba(201,149,42,0.10) 0%, transparent 40%),
      radial-gradient(ellipse at 95% 90%, rgba(201,149,42,0.10) 0%, transparent 40%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='0' y='0' width='60' height='60' fill='none' stroke='rgba(201%2C149%2C42%2C0.05)' stroke-width='1'/%3E%3Cline x1='15' y1='0' x2='15' y2='60' stroke='rgba(201%2C149%2C42%2C0.03)' stroke-width='1'/%3E%3Cline x1='30' y1='0' x2='30' y2='60' stroke='rgba(201%2C149%2C42%2C0.03)' stroke-width='1'/%3E%3Cline x1='45' y1='0' x2='45' y2='60' stroke='rgba(201%2C149%2C42%2C0.03)' stroke-width='1'/%3E%3Cline x1='0' y1='15' x2='60' y2='15' stroke='rgba(201%2C149%2C42%2C0.03)' stroke-width='1'/%3E%3Cline x1='0' y1='30' x2='60' y2='30' stroke='rgba(201%2C149%2C42%2C0.03)' stroke-width='1'/%3E%3Cline x1='0' y1='45' x2='60' y2='45' stroke='rgba(201%2C149%2C42%2C0.03)' stroke-width='1'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  max-width: 860px;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const Sanskrit = styled.h2`
  font-size: 2.2rem;
  color: rgba(232,184,75,0.65);
  margin-bottom: 0.6rem;
  font-family: 'Playfair Display', serif;
  font-weight: 400;
  font-style: italic;
`;

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(3rem, 6vw, 4.8rem);
  font-weight: 900;
  margin-bottom: 0.6rem;
  line-height: 1.05;
  background: linear-gradient(135deg, #fff 0%, ${GOLD_LIGHT} 50%, #fff 100%);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 6s ease infinite, ${float} 7s ease-in-out infinite;
`;

const HeroSub = styled.h3`
  font-size: 1.15rem;
  color: rgba(255,255,255,0.65);
  font-weight: 400;
  font-family: 'Source Serif 4', serif;
  margin-top: 0.8rem;
`;

const GoldRule = styled.div`
  width: 60px;
  height: 1.5px;
  background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
  margin: 1.4rem auto;
  border-radius: 2px;
`;

const MainContent = styled.div`
  max-width: 1100px;
  margin: -4rem auto 0;
  padding: 0 2rem 5rem;
  position: relative;
  z-index: 2;
`;

const Section = styled.section`
  background: ${WARM_WHITE};
  border-radius: 20px;
  padding: 2.8rem;
  margin-bottom: 2rem;
  box-shadow: 0 8px 28px rgba(201,149,42,0.09);
  border: 1px solid rgba(201,149,42,0.12);
`;

const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: clamp(1.6rem, 2.5vw, 2.1rem);
  color: ${TEXT};
  margin-bottom: 1.8rem;
  text-align: center;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 52px;
    height: 2px;
    background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
    border-radius: 2px;
    margin: 0.75rem auto 0;
  }
`;

const Text = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: ${TEXT_MID};
  margin-bottom: 1.2rem;
  font-family: 'Source Serif 4', serif;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const FeatureCard = styled.div`
  background: ${CREAM};
  border-radius: 16px;
  padding: 2rem;
  border-left: 3px solid ${GOLD};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 32px rgba(201,149,42,0.13);
  }

  h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.2rem;
    color: ${TEXT};
    margin-bottom: 0.6rem;
  }
`;

const TechList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.2rem;
  margin-top: 1.5rem;
`;

const TechItem = styled.div`
  background: ${CREAM};
  padding: 1.8rem;
  border-radius: 14px;
  border-left: 3px solid ${GOLD};
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  transition: all 0.3s ease;

  .tech-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(201,149,42,0.09);
    border: 1px solid rgba(201,149,42,0.15);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .tech-text h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.05rem;
    color: ${TEXT};
    margin-bottom: 0.4rem;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(201,149,42,0.12);
  }
`;

const ComingSoonSection = styled(Section)`
  text-align: center;
  padding: 4rem 3rem;
  background: linear-gradient(135deg, rgba(201,149,42,0.05) 0%, rgba(232,184,75,0.05) 100%);
  border: 1.5px dashed rgba(201,149,42,0.35);
`;

const ComingSoonTitle = styled(SectionTitle)`
  font-size: clamp(2rem, 4vw, 3rem);
  background: linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Anubhuti = () => (
  <PageContainer>
    <Hero>
      <HeroContent>
        <Sanskrit>अनुभूति</Sanskrit>
        <HeroTitle>Anubhūti</HeroTitle>
        <GoldRule />
        <HeroSub>Interactive and Immersive Experience</HeroSub>
      </HeroContent>
    </Hero>

    <MainContent>
      <Section>
        <SectionTitle>Visualization Reimagined</SectionTitle>
        <Text>
          Anubhūti transforms Indian cultural heritage sites into interactive, immersive experiences
          using cutting-edge AI, 3D reconstruction, and satellite imagery.
        </Text>
        <FeatureGrid>
          <FeatureCard>
            <GoldIcon>
              {/* Eye — AI Recognition */}
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 9v1M12 14v1"/>
            </GoldIcon>
            <h3>AI-Powered Recognition</h3>
            <Text>Custom ML models trained specifically for Indian architectural elements and vegetation patterns.</Text>
          </FeatureCard>
          <FeatureCard>
            <GoldIcon>
              {/* 3D box — Reconstruction */}
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
              <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
              <line x1="12" y1="22.08" x2="12" y2="12"/>
            </GoldIcon>
            <h3>3D Reconstruction</h3>
            <Text>Advanced automated pipeline for processing heritage structures at varying levels of detail.</Text>
          </FeatureCard>
          <FeatureCard>
            <GoldIcon>
              {/* Network — Semantic Integration */}
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </GoldIcon>
            <h3>Semantic Integration</h3>
            <Text>Rich metadata about cultural and historical significance embedded within each 3D model.</Text>
          </FeatureCard>
        </FeatureGrid>
      </Section>

      <Section>
        <SectionTitle>Key Features</SectionTitle>
        <TechList>
          <TechItem>
            <div className="tech-icon">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                {/* Map */}
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                <line x1="8" y1="2" x2="8" y2="18"/>
                <line x1="16" y1="6" x2="16" y2="22"/>
              </svg>
            </div>
            <div className="tech-text">
              <h3>Pan-India Coverage</h3>
              <Text style={{ marginBottom: 0 }}>Comprehensive geographical accessibility across the entire Indian subcontinent.</Text>
            </div>
          </TechItem>
          <TechItem>
            <div className="tech-icon">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                {/* Layers */}
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
            </div>
            <div className="tech-text">
              <h3>Interoperable Models</h3>
              <Text style={{ marginBottom: 0 }}>Fully customizable 3D models compatible with industry-standard formats.</Text>
            </div>
          </TechItem>
          <TechItem>
            <div className="tech-icon">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={GOLD} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                {/* Zap / rapid */}
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <div className="tech-text">
              <h3>Rapid Processing</h3>
              <Text style={{ marginBottom: 0 }}>Quick generation of 3D tiles and 360° immersive videos.</Text>
            </div>
          </TechItem>
        </TechList>
      </Section>

      <ComingSoonSection>
        <ComingSoonTitle>Coming Soon</ComingSoonTitle>
        <Text style={{ fontSize: '1.05rem', maxWidth: '500px', margin: '1.5rem auto 0' }}>
          We're working on something exciting. Stay tuned for updates!
        </Text>
      </ComingSoonSection>
    </MainContent>
  </PageContainer>
);

export default Anubhuti;
